'use client';

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { Presentation, Slide, SlideElement, PresentationSource, ShapeType } from '../lib/types';
import { Toolbar } from './Toolbar';
import { Sidebar } from './Sidebar';
import { EditorCanvas } from './EditorCanvas';
import { PptxExporter } from '../lib/pptx-exporter';
import { PptxParser } from '../lib/pptx-parser';
import { PresenterMode } from './PresenterMode';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { RefineMenu } from './RefineMenu';
import { SlashMenu } from './SlashMenu';
import { Sparkles, Plus, RefreshCw, Check, Trash2, Image as ImageIcon, PieChart } from 'lucide-react';
import { cn } from '../lib/utils';
import { Snackbar, SnackbarType } from './Snackbar';

interface PptEditorProps {
    initialPresentation?: Presentation;
    url?: string;
    appName?: string;
    onChange?: (presentation: Presentation) => void;
    geminiApiKey?: string;
    width: number | string;
    height: number | string;
    appBgColor?: string;
    showHomeOnEmpty?: boolean;
    initialSource?: PresentationSource;
    onSourceChange?: (source: PresentationSource, url?: string) => void;
    proxyUrl?: string;
    onGenerateImage?: (prompt: string) => Promise<string>;
    onGenerateInfographic?: (prompt: string) => Promise<string>;
    onAiRewrite?: (text: string) => Promise<string>;
    onAiGrammar?: (text: string) => Promise<string>;
    onAiShorten?: (text: string) => Promise<string>;
    onAiLengthen?: (text: string) => Promise<string>;
    onAiContinue?: (text: string) => Promise<string>;
    isTwoStepInfographicGeneration?: boolean;
    onRefineInfographicPrompt?: (text: string) => Promise<string>;
    onRefineShorten?: (text: string) => Promise<string>;
    onRefineReframe?: (text: string) => Promise<string>;
    onRefineLengthen?: (text: string) => Promise<string>;
    onAiEdit?: (text: string, prompt: string) => Promise<string>;
    /**
     * Optional custom fetcher for presentation files.
     * Useful for fetching large files via pre-signed URLs to avoid 413 Payload Too Large errors.
     * If provided, this function handles the fetching instead of the default proxy/fetch logic.
     */
    fetchPresentation?: (url: string) => Promise<ArrayBuffer | Blob>;
}

export const PptEditor: React.FC<PptEditorProps> = ({
    initialPresentation, url, appName = 'My Studio', onChange, geminiApiKey = process.env.NEXT_PUBLIC_GEMINI_API_KEY || "",
    width, height, appBgColor = '#B7472A', showHomeOnEmpty = false,
    initialSource, onSourceChange, proxyUrl, onGenerateImage, onGenerateInfographic,
    onAiRewrite, onAiGrammar, onAiShorten, onAiLengthen, onAiContinue,
    isTwoStepInfographicGeneration, onRefineInfographicPrompt,
    onRefineShorten, onRefineReframe, onRefineLengthen,
    onAiEdit, fetchPresentation
}) => {
    const [presentation, setPresentation] = useState<Presentation>(
        initialPresentation || { slides: [{ id: 'slide-1', elements: [] }], layout: { width: 12192000, height: 6858000 } }
    );

    const [history, setHistory] = useState<Presentation[]>([]);
    const [redoStack, setRedoStack] = useState<Presentation[]>([]);

    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [askAiResponse, setAskAiResponse] = useState<string | null>(null);
    const [refineResponse, setRefineResponse] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [source, setSource] = useState<PresentationSource | null>(initialSource || (url ? 'url' : null));
    const [showInitialLayout, setShowInitialLayout] = useState(showHomeOnEmpty && !initialPresentation && !url && !initialSource);
    const [urlInput, setUrlInput] = useState('');
    const [slashMenu, setSlashMenu] = useState<{
        visible: boolean,
        position: { x: number, y: number },
        mode: 'default' | 'textbox' | 'text-selection',
        context?: any
    }>({
        visible: false,
        position: { x: 0, y: 0 },
        mode: 'default'
    });
    const [isImageGenOpen, setIsImageGenOpen] = useState(false);
    const [imageGenPrompt, setImageGenPrompt] = useState('');
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [pendingImage, setPendingImage] = useState<string | null>(null);
    const [imageError, setImageError] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [isInfographicGenOpen, setIsInfographicGenOpen] = useState(false);
    const [infographicGenPrompt, setInfographicGenPrompt] = useState('');
    const [isGeneratingInfographic, setIsGeneratingInfographic] = useState(false);
    const [pendingInfographic, setPendingInfographic] = useState<string | null>(null);
    const [infographicError, setInfographicError] = useState(false);
    const [isRefiningInfographic, setIsRefiningInfographic] = useState(false);
    const [refinedInfographicPrompt, setRefinedInfographicPrompt] = useState<string | null>(null);
    const [showInfographicPreview, setShowInfographicPreview] = useState(false);

    // Snackbar State
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; type: SnackbarType }>({
        open: false,
        message: '',
        type: 'info'
    });

    const showSnackbar = (message: string, type: SnackbarType = 'info') => {
        setSnackbar({ open: true, message, type });
    };

    useEffect(() => {
        if (pendingImage) {
            setImageError(false);
            setPreviewLoading(true);
        }
    }, [pendingImage]);

    useEffect(() => {
        if (pendingInfographic) {
            setInfographicError(false);
            setPreviewLoading(true);
        }
    }, [pendingInfographic]);

    useEffect(() => {
        if (url) {
            handleLoadPresentation(url);
        }
    }, [url]);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [lastAiPrompt, setLastAiPrompt] = useState<{ id: string, action: 'shorten' | 'reframe' | 'lengthen' | 'rewrite' | 'grammar' | 'continue' | 'custom' } | null>(null);

    // Clear AI states when selection changes
    useEffect(() => {
        setAskAiResponse(null);
        setRefineResponse(null);
        setLastAiPrompt(null);
        setIsAiLoading(false);
    }, [selectedElementId]);

    // Calculate UI scale based on width/height
    const uiScale = useMemo(() => {
        const w = typeof width === 'number' ? width : parseInt(String(width));
        if (isNaN(w)) return 1;
        // Base width for 1x scale is 1600px
        // Range: 0.4 to 1.0 (never scale up beyond 1x)
        return Math.min(Math.max(w / 1600, 0.7), 1);
    }, [width]);

    const currentSlide = presentation.slides[currentSlideIndex];

    const selectedElement = useMemo(() => {
        return currentSlide.elements.find(el => el.id === selectedElementId) || null;
    }, [currentSlide, selectedElementId]);

    const saveToHistory = useCallback((state: Presentation) => {
        setHistory(prevHistory => [...prevHistory.slice(-19), presentation]);
        setRedoStack([]);
        setPresentation(state);
        onChange?.(state);
    }, [onChange, presentation]);

    const undo = () => {
        if (history.length === 0) return;
        const last = history[history.length - 1];
        setRedoStack(prev => [...prev, presentation]);
        setHistory(prev => prev.slice(0, -1));
        setPresentation(last);
    };

    const redo = () => {
        if (redoStack.length === 0) return;
        const next = redoStack[redoStack.length - 1];
        setHistory(prev => [...prev, presentation]);
        setRedoStack(prev => prev.slice(0, -1));
        setPresentation(next);
    };

    const handleUpdateSlide = useCallback((updates: Partial<Slide>) => {
        setPresentation(prev => {
            const newSlides = [...prev.slides];
            newSlides[currentSlideIndex] = { ...newSlides[currentSlideIndex], ...updates };
            const updated = { ...prev, slides: newSlides };

            // Note: We can't call saveToHistory inside here easily without dependency issues, 
            // but we can move history logic here if needed or keep it simple for now.
            // For now, let's just make it stable.
            setHistory(h => [...h.slice(-19), prev]);
            setRedoStack([]);
            onChange?.(updated);
            return updated;
        });
    }, [currentSlideIndex, onChange]);

    const handleElementUpdate = useCallback((elementId: string, updates: Partial<SlideElement>) => {
        setPresentation(prev => {
            const slide = prev.slides[currentSlideIndex];
            const newElements = slide.elements.map(el =>
                el.id === elementId ? { ...el, ...updates } as SlideElement : el
            );
            const newSlides = [...prev.slides];
            newSlides[currentSlideIndex] = { ...slide, elements: newElements };
            const updated = { ...prev, slides: newSlides };
            onChange?.(updated);
            return updated;
        });
    }, [currentSlideIndex, onChange]);

    const handleFormatText = (updates: any) => {
        if (!selectedElementId) return;
        const el = currentSlide.elements.find(e => e.id === selectedElementId);
        if (el) {
            handleElementUpdate(selectedElementId, updates);
            // Explicitly save to history for discrete formatting actions
            const slide = presentation.slides[currentSlideIndex];
            const newElements = slide.elements.map(e => e.id === selectedElementId ? { ...e, ...updates } : e);
            const newSlides = [...presentation.slides];
            newSlides[currentSlideIndex] = { ...slide, elements: newElements };
            saveToHistory({ ...presentation, slides: newSlides });
        }
    };

    const handleDeleteElement = useCallback(() => {
        if (!selectedElementId) return;
        const slide = presentation.slides[currentSlideIndex];
        const newElements = slide.elements.filter(el => el.id !== selectedElementId);
        handleUpdateSlide({ elements: newElements });
        setSelectedElementId(null);
    }, [selectedElementId, currentSlideIndex, presentation, handleUpdateSlide]);

    const handleAddText = () => {
        const newElement: SlideElement = {
            id: `text-${Date.now()}`,
            type: 'text',
            content: 'New Text',
            x: 100,
            y: 100,
            width: 400,
            height: 100,
            fontSize: 48,
            fontFamily: 'Arial',
            color: '#000000',
            zIndex: currentSlide.elements.length
        };
        handleUpdateSlide({ elements: [...currentSlide.elements, newElement] });
    };

    const handleAddImage = useCallback(async (input: File | string) => {
        let src = '';
        if (typeof input === 'string') {
            src = input;
        } else {
            src = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target?.result as string);
                reader.readAsDataURL(input);
            });
        }
        const id = `img-${Date.now()}`;
        const newElement: SlideElement = {
            id,
            type: 'image',
            src: src,
            x: 100,
            y: 100,
            width: 400,
            height: 400,
            zIndex: currentSlide.elements.length
        };

        // Auto-scale to fit if too large
        const img = new Image();
        img.onload = () => {
            const MAX_SIZE = 800;
            let w = img.width;
            let h = img.height;
            if (w > MAX_SIZE || h > MAX_SIZE) {
                const ratio = w / h;
                if (w > h) { w = MAX_SIZE; h = MAX_SIZE / ratio; }
                else { h = MAX_SIZE; w = MAX_SIZE * ratio; }
            }
            // Update the element with actual dimensions
            handleElementUpdate(id, { width: w, height: h });
        };
        img.src = src;

        handleUpdateSlide({ elements: [...currentSlide.elements, newElement] });
        setSelectedElementId(id);
    }, [currentSlide.elements, handleUpdateSlide, handleElementUpdate, setSelectedElementId]);

    const handleAddShape = (shapeType: ShapeType) => {
        const newElement: SlideElement = {
            id: `shape-${Date.now()}`,
            type: 'shape',
            shapeType,
            fill: '#B7472A',
            x: 300,
            y: 300,
            width: 200,
            height: 200,
            zIndex: currentSlide.elements.length
        };
        handleUpdateSlide({ elements: [...currentSlide.elements, newElement] });
    }

    const handleAddSlide = () => {
        const newSlide: Slide = { id: `slide-${Date.now()}`, elements: [] };
        saveToHistory({ ...presentation, slides: [...presentation.slides, newSlide] });
        setCurrentSlideIndex(presentation.slides.length);
    };

    const handleDeleteSlide = (index: number) => {
        if (presentation.slides.length <= 1) return;

        const newSlides = presentation.slides.filter((_, i) => i !== index);
        saveToHistory({ ...presentation, slides: newSlides });

        // Adjust current slide index if needed
        if (index <= currentSlideIndex) {
            setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1));
        }
    };

    const handleDuplicateSlide = (index: number) => {
        const slideToClone = presentation.slides[index];
        const newSlide: Slide = {
            ...slideToClone,
            id: `slide-${Date.now()}`,
            elements: slideToClone.elements.map(el => ({
                ...el,
                id: `${el.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            }))
        };
        const newSlides = [...presentation.slides];
        newSlides.splice(index + 1, 0, newSlide);
        saveToHistory({ ...presentation, slides: newSlides });
        setCurrentSlideIndex(index + 1);
    };

    const handleReorderSlides = (oldIndex: number, newIndex: number) => {
        if (oldIndex === newIndex) return;
        const newSlides = [...presentation.slides];
        const [removed] = newSlides.splice(oldIndex, 1);
        newSlides.splice(newIndex, 0, removed);

        saveToHistory({ ...presentation, slides: newSlides });

        if (currentSlideIndex === oldIndex) {
            setCurrentSlideIndex(newIndex);
        } else if (currentSlideIndex > oldIndex && currentSlideIndex <= newIndex) {
            setCurrentSlideIndex(currentSlideIndex - 1);
        } else if (currentSlideIndex < oldIndex && currentSlideIndex >= newIndex) {
            setCurrentSlideIndex(currentSlideIndex + 1);
        }
    };

    const handleApplyLayout = (layoutType: 'title' | 'content' | 'split') => {
        let elements: SlideElement[] = [];
        const timestamp = Date.now();

        if (layoutType === 'title') {
            elements = [
                {
                    id: `text-title-${timestamp}`,
                    type: 'text',
                    content: 'Presentation Title',
                    x: 100,
                    y: 200,
                    width: 1000,
                    height: 150,
                    fontSize: 80,
                    fontFamily: 'Inter',
                    color: '#000000',
                    textAlign: 'center',
                    isBold: true,
                    zIndex: 0
                },
                {
                    id: `text-sub-${timestamp}`,
                    type: 'text',
                    content: 'Subtitle goes here',
                    x: 200,
                    y: 380,
                    width: 800,
                    height: 80,
                    fontSize: 36,
                    fontFamily: 'Inter',
                    color: '#64748b',
                    textAlign: 'center',
                    zIndex: 1
                }
            ];
        } else if (layoutType === 'content') {
            elements = [
                {
                    id: `text-title-${timestamp}`,
                    type: 'text',
                    content: 'Slide Title',
                    x: 60,
                    y: 40,
                    width: 800,
                    height: 80,
                    fontSize: 48,
                    fontFamily: 'Inter',
                    color: '#000000',
                    isBold: true,
                    zIndex: 0
                },
                {
                    id: `text-body-${timestamp}`,
                    type: 'text',
                    content: '• Add your points here\n• Press Enter for new line\n• Use the toolbar to format',
                    x: 60,
                    y: 150,
                    width: 1080,
                    height: 480,
                    fontSize: 28,
                    fontFamily: 'Inter',
                    color: '#334155',
                    zIndex: 1,
                    isBulleted: true
                }
            ];
        } else if (layoutType === 'split') {
            elements = [
                {
                    id: `text-title-${timestamp}`,
                    type: 'text',
                    content: 'Comparison Layout',
                    x: 60,
                    y: 40,
                    width: 1080,
                    height: 80,
                    fontSize: 48,
                    fontFamily: 'Inter',
                    color: '#000000',
                    isBold: true,
                    textAlign: 'center',
                    zIndex: 0
                },
                {
                    id: `text-left-${timestamp}`,
                    type: 'text',
                    content: 'Left column content goes here.',
                    x: 60,
                    y: 180,
                    width: 520,
                    height: 400,
                    fontSize: 24,
                    fontFamily: 'Inter',
                    color: '#334155',
                    zIndex: 1
                },
                {
                    id: `text-right-${timestamp}`,
                    type: 'text',
                    content: 'Right column content goes here.',
                    x: 620,
                    y: 180,
                    width: 520,
                    height: 400,
                    fontSize: 24,
                    fontFamily: 'Inter',
                    color: '#334155',
                    zIndex: 2
                }
            ];
        }

        handleUpdateSlide({ elements });
    };

    const handleAiAction = async (id: string, action: 'shorten' | 'reframe' | 'lengthen') => {
        const slide = presentation.slides[currentSlideIndex];
        const element = slide.elements.find(el => el.id === id);
        if (!element || element.type !== 'text') return;

        setIsAiLoading(true);
        setRefineResponse(null);
        setLastAiPrompt({ id, action });

        try {
            let resultText = '';

            // 1. Try Custom Props (Specific to Toolbar Refine actions)
            if (action === 'shorten' && onRefineShorten) resultText = await onRefineShorten(element.content);
            else if (action === 'reframe' && onRefineReframe) resultText = await onRefineReframe(element.content);
            else if (action === 'lengthen' && onRefineLengthen) resultText = await onRefineLengthen(element.content);

            // 2. Fallback to Gemini
            if (!resultText) {
                if (!geminiApiKey) {
                    showSnackbar("Gemini API key is missing and no custom AI route provided.", 'error');
                    setIsAiLoading(false);
                    return;
                }
                const genAI = new GoogleGenerativeAI(geminiApiKey);
                const model = genAI.getGenerativeModel({
                    model: "gemini-flash-latest",
                    systemInstruction: "You are a professional presentation assistant. Your task is to transform slide text according to specific user requests (shorten, reframe, or lengthen). \n\nCRITICAL RULES:\n1. Return ONLY the transformed text. \n2. Do NOT include any explanations, choices, or conversational fillers.\n3. Do NOT use markdown formatting (like bolding or headers) unless specifically needed for bullet points.\n4. If the input text is a title/headline, the output should be a professional title/headline.\n5. If the input text is a bulleted list, the output MUST be a bulleted list using the same bullet style (e.g., '•').\n6. Maintain the professional tone of the original presentation."
                });

                const elementContext = element.fontSize > 32 ? "Title/Headline" : "Body Text/List";
                const prompt = `Action: ${action.toUpperCase()}\nText Role: ${elementContext}\nOriginal Text: "${element.content}"\n\nTransformed Text:`;

                const result = await model.generateContent(prompt);
                const response = await result.response;
                resultText = response.text().trim();
            }

            setRefineResponse(resultText);
        } catch (error) {
            console.error("Gemini AI Error:", error);
            showSnackbar("Error generating response. Please check your API key or connection.", 'error');
        } finally {
            setIsAiLoading(false);
        }
    };

    const handleAiCustomEdit = async (prompt: string) => {
        const slide = presentation.slides[currentSlideIndex];
        const element = slide.elements.find(el => el.id === selectedElementId);
        if (!element || element.type !== 'text') return;

        setIsAiLoading(true);
        setAskAiResponse(null);
        // We reuse the lastAiPrompt state but with a special action type if needed, 
        // or just map it to 'rewrite' for now as a generic placeholder since we only store ID and Action.
        // Actually, we need to know it was a custom edit to support "Regenerate".
        // For simplicity, let's treat it as 'rewrite' but we might lose the prompt on regenerate.
        // TODO: Enhance lastAiPrompt to store custom prompts.
        setLastAiPrompt({ id: selectedElementId!, action: 'custom' });

        try {
            let resultText = '';

            // 1. Try Custom Prop
            if (onAiEdit) {
                resultText = await onAiEdit(element.content, prompt);
            }

            // 2. Fallback to Gemini
            if (!resultText) {
                if (!geminiApiKey) {
                    showSnackbar("Gemini API key is missing and no custom AI route provided.", 'error');
                    setIsAiLoading(false);
                    return;
                }

                const genAI = new GoogleGenerativeAI(geminiApiKey);
                const model = genAI.getGenerativeModel({ model: "gemini-flash-latest" });

                const systemPrompt = "You are a professional editor. Follow the user's instruction to edit the text. Return ONLY the edited text. No explanations.";
                const userPrompt = `Target Text: "${element.content}"\n\nInstruction: ${prompt}`;

                const result = await model.generateContent(`${systemPrompt}\n\n${userPrompt}`);
                resultText = result.response.text().trim();
            }

            setAskAiResponse(resultText);
        } catch (error) {
            console.error("AI Custom Edit Error:", error);
            showSnackbar("AI editing failed. Please check configuration.", 'error');
        } finally {
            setIsAiLoading(false);
        }
    };

    const handleAiTextEdit = async (action: 'rewrite' | 'grammar' | 'shorten' | 'lengthen' | 'continue') => {
        const slide = presentation.slides[currentSlideIndex];
        const element = slide.elements.find(el => el.id === selectedElementId);
        if (!element || element.type !== 'text') return;

        setIsAiLoading(true);
        setAskAiResponse(null);
        setLastAiPrompt({ id: selectedElementId!, action: action as any }); // Cast for now, might need to update type

        try {
            let resultText = '';

            // 1. Try Custom Props
            if (action === 'rewrite' && onAiRewrite) resultText = await onAiRewrite(element.content);
            else if (action === 'grammar' && onAiGrammar) resultText = await onAiGrammar(element.content);
            else if (action === 'shorten' && onAiShorten) resultText = await onAiShorten(element.content);
            else if (action === 'lengthen' && onAiLengthen) resultText = await onAiLengthen(element.content);
            else if (action === 'continue' && onAiContinue) resultText = await onAiContinue(element.content);

            // 2. Fallback to Gemini if no custom prop or if custom prop returned empty (optional behavior, assumed strictly prop OR gemini)
            if (!resultText) {
                if (!geminiApiKey) {
                    showSnackbar("Gemini API key is missing and no custom AI route provided.", 'error');
                    setIsAiLoading(false);
                    return;
                }

                const genAI = new GoogleGenerativeAI(geminiApiKey);
                const model = genAI.getGenerativeModel({ model: "gemini-flash-latest" });

                let systemPrompt = "You are a professional editor. Return ONLY the edited text. No explanations.";
                let userPrompt = `original text: "${element.content}"`;

                switch (action) {
                    case 'rewrite': userPrompt = `Improve the writing of: "${element.content}"`; break;
                    case 'grammar': userPrompt = `Fix grammar in: "${element.content}"`; break;
                    case 'shorten': userPrompt = `Make this shorter: "${element.content}"`; break;
                    case 'lengthen': userPrompt = `Make this longer: "${element.content}"`; break;
                    case 'continue': userPrompt = `Continue writing from: "${element.content}"`; break;
                }

                const result = await model.generateContent(`${systemPrompt}\n\n${userPrompt}`);
                resultText = result.response.text().trim();
            }

            setAskAiResponse(resultText);
        } catch (error) {
            console.error("AI Text Edit Error:", error);
            showSnackbar("AI editing failed. Please check configuration.", 'error');
        } finally {
            setIsAiLoading(false);
        }
    };

    const handleAiResponseAction = (action: 'replace' | 'addBelow' | 'discard' | 'regenerate') => {
        if (!askAiResponse || !lastAiPrompt) return;

        if (action === 'regenerate') {
            handleAiTextEdit(lastAiPrompt.action as any);
            return;
        }

        const slide = presentation.slides[currentSlideIndex];
        const originalElement = slide.elements.find(el => el.id === lastAiPrompt.id);
        if (!originalElement || originalElement.type !== 'text') return;

        if (action === 'replace') {
            handleElementUpdate(lastAiPrompt.id, { content: askAiResponse });
        } else if (action === 'addBelow') {
            const newElement: SlideElement = {
                ...originalElement,
                id: `text-ai-${Date.now()}`,
                content: askAiResponse,
                y: originalElement.y + originalElement.height + 20,
                zIndex: slide.elements.length
            };
            handleUpdateSlide({ elements: [...slide.elements, newElement] });
        }

        setAskAiResponse(null);
        setLastAiPrompt(null);
        setSlashMenu(prev => ({ ...prev, visible: false }));
    };

    const handleRefineResponseAction = (action: 'replace' | 'addBelow' | 'discard' | 'regenerate') => {
        if (!refineResponse || !lastAiPrompt) return;

        if (action === 'discard') {
            setRefineResponse(null);
            return;
        }

        if (action === 'regenerate') {
            const method = lastAiPrompt.action;
            if (method === 'shorten' || method === 'lengthen' || method === 'reframe') {
                handleAiAction(lastAiPrompt.id, method);
            }
            return;
        }

        const slide = presentation.slides[currentSlideIndex];
        const originalElement = slide.elements.find(el => el.id === lastAiPrompt.id);
        if (!originalElement || originalElement.type !== 'text') return;

        if (action === 'replace') {
            handleElementUpdate(lastAiPrompt.id, { content: refineResponse });
        } else if (action === 'addBelow') {
            const newElement: SlideElement = {
                ...originalElement,
                id: `text-refine-${Date.now()}`,
                content: refineResponse,
                y: originalElement.y + originalElement.height + 20,
                zIndex: slide.elements.length
            };
            handleUpdateSlide({ elements: [...slide.elements, newElement] });
        }

        setRefineResponse(null);
        setLastAiPrompt(null);
    };

    const handleExport = async () => {
        const exporter = new PptxExporter();
        await exporter.export(presentation);
    };

    const handlePlay = () => {
        setIsPreviewMode(true);
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        }
    };

    const handleClosePresenter = () => {
        setIsPreviewMode(false);
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                setIsPreviewMode(false);
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Delete' || e.key === 'Backspace') {
                const activeElement = document.activeElement;
                if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA' || (activeElement as any)?.isContentEditable) return;
                handleDeleteElement();
            }
            if (e.key === 'Escape') {
                setSelectedElementId(null);
                setSlashMenu(prev => ({ ...prev, visible: false }));
            }
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'z') {
                    e.preventDefault();
                    if (e.shiftKey) redo(); else undo();
                }
                if (e.key === 'y') {
                    e.preventDefault();
                    redo();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleDeleteElement, undo, redo]);

    const updateSource = (newSource: PresentationSource, url?: string) => {
        setSource(newSource);
        onSourceChange?.(newSource, url);
    };

    const handleNewPresentation = () => {
        setPresentation({
            slides: [{ id: `slide-${Date.now()}`, elements: [] }],
            layout: { width: 12192000, height: 6858000 }
        });
        setCurrentSlideIndex(0);
        setSelectedElementId(null);
        updateSource('scratch');
    };

    const handleLoadPresentation = async (input: File | string) => {
        setIsLoading(true);
        try {
            const parser = new PptxParser();
            let prompt: File | string | ArrayBuffer | Blob = input;
            if (typeof input === 'string') {
                const trimmedInput = input.trim();

                // If a custom fetcher is provided, use it to get the blob/buffer directly
                if (fetchPresentation) {
                    prompt = await fetchPresentation(trimmedInput);
                } else {
                    // Otherwise fall back to the proxyUrl or direct fetch logic
                    prompt = proxyUrl
                        ? `${proxyUrl}${proxyUrl.includes('?') ? '&' : '?'}url=${encodeURIComponent(trimmedInput)}`
                        : trimmedInput;
                }
                updateSource('url', trimmedInput);
            } else {
                updateSource('uploaded');
            }
            const result = await parser.parse(prompt);
            setPresentation(result);
            setHistory([]);
            setRedoStack([]);
            setCurrentSlideIndex(0);
            setSelectedElementId(null);
        } catch (err) {
            console.error('Failed to load PPTX', err);
            showSnackbar('Failed to load presentation. Please ensure it is a valid .pptx.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSlashAction = (actionId: string, payload?: any) => {
        if (actionId !== 'ai-text-edit' && actionId !== 'ai-custom-edit') {
            setSlashMenu(prev => ({ ...prev, visible: false }));
        }

        switch (actionId) {
            case 'add-text': handleAddText(); break;
            case 'add-image': handleUploadImageClick(); break;
            case 'add-shape': handleAddShape(payload as ShapeType); break;
            case 'new-slide': handleAddSlide(); break;
            case 'ai-action': handleAiAction(selectedElementId || '', payload); break;
            case 'ai-text-edit': handleAiTextEdit(payload); break;
            case 'ai-custom-edit': handleAiCustomEdit(payload); break;
            case 'generate-image': setIsImageGenOpen(true); break;
            case 'generate-infographic': handleOpenInfographic(); break;
            case 'format': handleFormatText(payload); break;
            case 'delete': handleDeleteElement(); break;
        }
    };

    const handleGenerateImage = async () => {
        if (!imageGenPrompt.trim()) return;
        setIsGeneratingImage(true);
        try {
            let imageUrl = '';
            if (onGenerateImage) {
                imageUrl = await onGenerateImage(imageGenPrompt);
            } else if (geminiApiKey) {
                // Fallback to internal Gemini if key is provided (mocking for now as Gemini doesn't have a direct image gen API via this specific JS SDK easily without Vertex AI)
                // In a real scenario, this would call a known endpoint.
                throw new Error("Internal image generation requires onGenerateImage prop or specific endpoint configuration.");
            } else {
                throw new Error("No image generation function or Gemini API key provided.");
            }
            setPendingImage(imageUrl);
            setIsImageGenOpen(false);
            setImageGenPrompt('');
        } catch (error) {
            console.error("Image generation failed:", error);
            showSnackbar("Image generation failed: " + (error instanceof Error ? error.message : "Unknown error"), 'error');
        } finally {
            setIsGeneratingImage(false);
        }
    };

    const handleFinalizeImage = (action: 'insert' | 'replace' | 'discard') => {
        if (action === 'insert' && pendingImage) {
            handleAddImage(pendingImage);
        } else if (action === 'replace' && pendingImage && selectedElementId) {
            const el = presentation.slides[currentSlideIndex].elements.find(e => e.id === selectedElementId);
            if (el && el.type === 'image') {
                handleElementUpdate(selectedElementId, { src: pendingImage });
            } else {
                handleAddImage(pendingImage); // Fallback to insert if not an image
            }
        }
        setPendingImage(null);
    };

    const handleGenerateInfographic = async (customPrompt?: string) => {
        const promptToUse = customPrompt || infographicGenPrompt;
        if (!promptToUse.trim()) return;
        setIsGeneratingInfographic(true);
        setShowInfographicPreview(false);
        setPendingInfographic(null);
        try {
            let infographicUrl = '';
            if (onGenerateInfographic) {
                infographicUrl = await onGenerateInfographic(promptToUse);
            } else if (geminiApiKey) {
                // Fallback / Mock
                // throw new Error("Internal info gen...");
                // For now, let's just mock it or throw if not provided, similar to image gen
                throw new Error("Internal infographic generation requires onGenerateInfographic prop or specific endpoint configuration.");
            } else {
                throw new Error("No infographic generation function or Gemini API key provided.");
            }
            setPendingInfographic(infographicUrl);

            // Preload image to ensure loading modal covers the image loading time
            const img = new Image();
            img.src = proxyUrl ? `${proxyUrl}${encodeURIComponent(infographicUrl)}` : infographicUrl;

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = () => {
                    // Even if it fails, we proceed to show the preview modal (which handles errors)
                    // or we could throw here. Let's proceed to allow the preview modal's UI to specific error.
                    console.log("Image preload failed, proceeding to preview");
                    resolve(null);
                };
            });

            setShowInfographicPreview(true);
            setIsInfographicGenOpen(false);
            setInfographicGenPrompt('');
        } catch (error) {
            console.error("Infographic generation failed:", error);
            showSnackbar("Infographic generation failed: " + (error instanceof Error ? error.message : "Unknown error"), 'error');
        } finally {
            setIsGeneratingInfographic(false);
        }
    };

    const handleFinalizeInfographic = (action: 'insert' | 'replace' | 'discard') => {
        if (action === 'insert' && pendingInfographic) {
            handleAddImage(pendingInfographic); // Treat as image
        } else if (action === 'replace' && pendingInfographic && selectedElementId) {
            const el = presentation.slides[currentSlideIndex].elements.find(e => e.id === selectedElementId);
            if (el && el.type === 'image') {
                handleElementUpdate(selectedElementId, { src: pendingInfographic });
            } else {
                handleAddImage(pendingInfographic);
            }
        }
        setPendingInfographic(null);
    };

    const handleOpenInfographic = async () => {
        if (isTwoStepInfographicGeneration && onRefineInfographicPrompt) {
            const textToRefine = selectedElement?.type === 'text' ? selectedElement.content : '';
            if (textToRefine) {
                setIsRefiningInfographic(true);
                try {
                    const refined = await onRefineInfographicPrompt(textToRefine);
                    setRefinedInfographicPrompt(refined);
                } catch (error) {
                    console.error("Prompt refinement failed:", error);
                    showSnackbar("Failed to refine infographic prompt.", 'error');
                } finally {
                    setIsRefiningInfographic(false);
                }
                return;
            }
        }
        setIsInfographicGenOpen(true);
    };

    const handleUploadImageClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                const src = await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (re) => resolve(re.target?.result as string);
                    reader.readAsDataURL(file);
                });
                setPendingImage(src);
            }
        };
        input.click();
    };

    const getProxiedUrl = useCallback((url: string) => {
        if (!proxyUrl || !url || url.startsWith('data:')) return url;
        if (url.includes('localhost') || url.includes('127.0.0.1')) return url;
        return `${proxyUrl}?url=${encodeURIComponent(url)}`;
    }, [proxyUrl]);

    if (!width || !height) {
        return (
            <div className="flex items-center justify-center bg-red-50 text-red-600 p-8 rounded-xl border-2 border-red-200 font-bold">
                Error: width and height props are compulsory for PptEditor.
            </div>
        );
    }

    return (
        <div
            className="flex flex-col bg-white relative"
            style={{
                width: typeof width === 'number' ? `${width}px` : width,
                height: typeof height === 'number' ? `${height}px` : height,
                fontSize: `${16 * uiScale}px`
            }}
        >
            {showInitialLayout && (
                <div className="absolute inset-0 z-[2000] bg-white flex flex-col items-center justify-center p-6 md:p-12 overflow-y-auto">
                    <div className="max-w-2xl w-full text-center space-y-6 md:space-y-8 animate-in fade-in zoom-in-95 duration-500 my-auto">
                        <div className="flex justify-center">
                            {/* <div className="p-6 rounded-3xl" style={{ backgroundColor: `${appBgColor}10` }}>
                                <div className="p-4 rounded-2xl" style={{ backgroundColor: appBgColor }}>
                                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
                                    </svg>
                                </div>
                            </div> */}
                        </div>
                        <div className="space-y-2">
                            <h2 className="text-4xl font-black tracking-tight text-slate-800">{appName}</h2>
                            <p className="text-slate-500 font-medium">Create stunning presentations in seconds.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                            <button
                                onClick={() => { handleNewPresentation(); setShowInitialLayout(false); }}
                                className="flex flex-col items-center justify-center gap-4 p-10 border-2 border-slate-100 rounded-[2.5rem] hover:border-slate-300 hover:bg-slate-50 transition-all group shadow-sm hover:shadow-md active:scale-[0.98]"
                            >
                                <div className="p-5 bg-slate-100 rounded-2xl group-hover:bg-blue-50 transition-colors">
                                    <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                    </svg>
                                </div>
                                <span className="text-sm font-black uppercase tracking-[0.15em] text-slate-600 group-hover:text-slate-900 transition-colors">Start New Deck</span>
                            </button>
                            <label className="flex flex-col items-center justify-center gap-4 p-10 border-2 border-slate-100 rounded-[2.5rem] hover:border-slate-300 hover:bg-slate-50 transition-all group shadow-sm hover:shadow-md active:scale-[0.98] cursor-pointer">
                                <div className="p-5 bg-slate-100 rounded-2xl group-hover:bg-emerald-50 transition-colors">
                                    <svg className="w-10 h-10 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                </div>
                                <span className="text-sm font-black uppercase tracking-[0.15em] text-slate-600 group-hover:text-slate-900 transition-colors">Upload PPTX</span>
                                <input
                                    type="file"
                                    accept=".pptx"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            handleLoadPresentation(file);
                                            setShowInitialLayout(false);
                                        }
                                    }}
                                />
                            </label>
                        </div>

                        {/* URL Input */}
                        <div className="space-y-4 pt-8 border-t border-slate-100">
                            <div className="flex gap-3 items-stretch">
                                <div className="flex-1 relative group">
                                    {/* <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-slate-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9h18" />
                                    </svg> */}
                                    <input
                                        type="text"
                                        placeholder="Paste presentation URL..."
                                        value={urlInput}
                                        onChange={(e) => setUrlInput(e.target.value)}
                                        className="text-black w-full bg-slate-50 border border-slate-200 rounded-2xl pl-12 pr-5 py-4 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-400/5 text-base transition-all h-full"
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        if (urlInput.trim()) {
                                            handleLoadPresentation(urlInput.trim());
                                            setShowInitialLayout(false);
                                        }
                                    }}
                                    disabled={!urlInput.trim() || isLoading}
                                    style={{ cursor: 'pointer' }}
                                    className="bg-slate-900 hover:bg-black disabled:bg-slate-100 disabled:text-slate-400 text-white font-black px-10 rounded-2xl transition-all shadow-sm text-sm shrink-0 whitespace-nowrap h-full min-h-[56px] flex items-center justify-center uppercase tracking-widest"
                                >
                                    {isLoading ? (
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : 'Load'}
                                </button>
                            </div>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">
                                Supports S3, public links, and versioned assets
                            </p>
                        </div>
                    </div>
                </div>
            )}
            {isPreviewMode && (
                <PresenterMode
                    presentation={presentation}
                    initialSlideIndex={currentSlideIndex}
                    onClose={handleClosePresenter}
                />
            )}
            <Toolbar
                onAddText={handleAddText}
                onAddImage={handleAddImage}
                onAddShape={handleAddShape}
                onAddSlide={handleAddSlide}
                onExport={handleExport}
                onFormatText={handleFormatText}
                onDeleteElement={handleDeleteElement}
                onGenerateImageClick={() => setIsImageGenOpen(true)}
                onGenerateInfographicClick={handleOpenInfographic}
                onUploadImageClick={handleUploadImageClick}
                onApplyLayout={handleApplyLayout}
                onPlay={() => setIsPreviewMode(true)}
                onAiAction={handleAiAction}
                onAiResponseAction={handleAiResponseAction}
                onNewPresentation={handleNewPresentation}
                onLoadPresentation={handleLoadPresentation}
                aiResponse={askAiResponse}
                isAiLoading={isAiLoading}
                selectedElement={selectedElement}
                refineResponse={refineResponse}
                onRefineAction={handleAiAction}
                appName={appName}
                appBgColor={appBgColor}
                uiScale={uiScale}
                source={source}
            />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar
                    slides={presentation.slides}
                    currentSlideIndex={currentSlideIndex}
                    onSelectSlide={setCurrentSlideIndex}
                    onDeleteSlide={handleDeleteSlide}
                    onDuplicateSlide={handleDuplicateSlide}
                    onReorderSlides={handleReorderSlides}
                    uiScale={uiScale}
                    appBgColor={appBgColor}
                />
                <div className="flex-1 flex items-center justify-center p-12 overflow-auto bg-white">
                    <EditorCanvas
                        slide={currentSlide}
                        onElementUpdate={handleElementUpdate}
                        onSelect={setSelectedElementId}
                        uiScale={uiScale}
                        onSlashCommand={(position, mode, context) => setSlashMenu({
                            visible: position.x !== 0,
                            position,
                            mode: mode || 'default',
                            context
                        })}
                        selectedElementId={selectedElementId}
                        onAiGenerateClick={() => setIsImageGenOpen(true)}
                        onAiGenerateInfographicClick={handleOpenInfographic}
                        onUploadImageClick={handleUploadImageClick}
                        onDeleteElement={handleDeleteElement}
                    />
                </div>
            </div>

            {slashMenu.visible && (
                <div
                    className="fixed inset-0 z-[9998]"
                    onClick={() => setSlashMenu(prev => ({ ...prev, visible: false }))}
                >
                    <SlashMenu
                        position={slashMenu.position}
                        onClose={() => setSlashMenu(prev => ({ ...prev, visible: false }))}
                        onAction={handleSlashAction}
                        canUseAi={!!geminiApiKey || !!proxyUrl}
                        mode={slashMenu.mode}
                        context={slashMenu.context}
                        aiResult={askAiResponse}
                        isAiLoading={isAiLoading}
                        onAiResponseAction={handleAiResponseAction}
                        lastAiAction={lastAiPrompt?.action}
                    />
                </div>
            )}

            {refineResponse && selectedElement && (
                <RefineMenu
                    result={refineResponse}
                    isLoading={isAiLoading}
                    onAction={handleRefineResponseAction}
                    onClose={() => setRefineResponse(null)}
                />
            )}

            {isImageGenOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-[500px] p-6 flex flex-col gap-4 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                                <Sparkles className="text-blue-600" size={20} />
                                Generate Image
                            </h3>
                            <button onClick={() => setIsImageGenOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>
                        <p className="text-sm text-slate-500">Describe the image you want to create for this slide.</p>
                        <textarea
                            autoFocus
                            placeholder="A futuristic city in the style of cyberpunk with neon lights..."
                            className="text-black w-full h-32 p-4 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium resize-none"
                            value={imageGenPrompt}
                            onChange={(e) => setImageGenPrompt(e.target.value)}
                        />
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsImageGenOpen(false)}
                                className="flex-1 px-5 py-3 cursor-pointer text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleGenerateImage}
                                disabled={isGeneratingImage || !imageGenPrompt.trim()}
                                className="flex-1 px-6 py-3 cursor-pointer text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-lg shadow-blue-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2"
                            >
                                {isGeneratingImage ? (
                                    <>
                                        <RefreshCw className="animate-spin" size={16} />
                                        Generating...
                                    </>
                                ) : (
                                    'Generate'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pendingImage && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white rounded-2xl shadow-3xl max-w-[90vw] max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 border border-slate-200">
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white">
                            <h3 className="text-lg font-bold text-slate-800">Preview Image</h3>
                            <button onClick={() => setPendingImage(null)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors">
                                <Plus className="rotate-45" size={20} />
                            </button>
                        </div>

                        <div className="p-8 bg-slate-100 flex items-center justify-center overflow-auto min-h-[300px] relative">
                            {previewLoading && !imageError && (
                                <div className="absolute inset-0 flex items-center justify-center bg-slate-100/50 z-10">
                                    <RefreshCw className="animate-spin text-slate-400" size={32} />
                                </div>
                            )}
                            <img
                                src={getProxiedUrl(pendingImage)}
                                alt="Generated"
                                className={cn(
                                    "max-w-full max-h-[60vh] rounded-lg shadow-2xl ring-4 ring-white transition-opacity duration-300",
                                    (imageError || previewLoading) ? "opacity-0" : "opacity-100"
                                )}
                                onLoad={() => setPreviewLoading(false)}
                                onError={() => {
                                    setImageError(true);
                                    setPreviewLoading(false);
                                }}
                            />
                            {imageError && (
                                <div className="flex flex-col items-center gap-4 text-slate-400 p-12 text-center animate-in fade-in duration-500">
                                    <div className="p-4 bg-slate-200/50 rounded-full">
                                        <ImageIcon size={48} className="opacity-40" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-slate-600">Failed to load preview</p>
                                        <p className="text-xs max-w-[300px] mt-1">The URL might be invalid or restricted. Ensure it's a valid image format.</p>
                                    </div>
                                    <code className="text-[10px] bg-slate-200/50 px-2 py-1 rounded break-all max-w-[400px]">
                                        {pendingImage}
                                    </code>
                                </div>
                            )}
                        </div>

                        <div className="p-6 bg-white border-t border-slate-100 flex flex-col gap-4">
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => handleFinalizeImage('insert')}
                                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-500/20 transition-all flex items-center justify-center gap-2"
                                >
                                    <Check size={18} />
                                    Insert image
                                </button>
                                {selectedElementId && presentation.slides[currentSlideIndex].elements.find(e => e.id === selectedElementId)?.type === 'image' && (
                                    <button
                                        onClick={() => handleFinalizeImage('replace')}
                                        className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-black rounded-xl font-bold shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2"
                                    >
                                        <RefreshCw size={18} />
                                        Replace existing
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => handleFinalizeImage('discard')}
                                className="w-full px-6 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                            >
                                <Trash2 size={18} />
                                Discard
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {isInfographicGenOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-[500px] p-6 flex flex-col gap-4 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                                <PieChart className="text-blue-600" size={20} />
                                Generate Infographic
                            </h3>
                            <button onClick={() => setIsInfographicGenOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>
                        <p className="text-sm text-slate-500">Describe the infographic you want to create for this slide.</p>
                        <textarea
                            autoFocus
                            placeholder="A pie chart showing market share distribution..."
                            className="text-black w-full h-32 p-4 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium resize-none"
                            value={infographicGenPrompt}
                            onChange={(e) => setInfographicGenPrompt(e.target.value)}
                        />
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsInfographicGenOpen(false)}
                                className="flex-1 px-5 py-3 cursor-pointer text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleGenerateInfographic()}
                                disabled={isGeneratingInfographic || !infographicGenPrompt.trim()}
                                className="flex-1 px-6 py-3 cursor-pointer text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-lg shadow-blue-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2"
                            >
                                {isGeneratingInfographic ? (
                                    <>
                                        <RefreshCw className="animate-spin" size={16} />
                                        Generating...
                                    </>
                                ) : (
                                    'Generate'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {(isGeneratingInfographic) && (
                <div className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/20 backdrop-blur-[2px] animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4 animate-in zoom-in-95 duration-200">
                        <RefreshCw className="animate-spin text-blue-600" size={32} />
                        <p className="text-sm font-bold text-slate-600">Generating Infographic...</p>
                    </div>
                </div>
            )}

            {isRefiningInfographic && (
                <div className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/20 backdrop-blur-[2px] animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4 animate-in zoom-in-95 duration-200">
                        <RefreshCw className="animate-spin text-blue-600" size={32} />
                        <p className="text-sm font-bold text-slate-600">Polishing your prompt with AI...</p>
                    </div>
                </div>
            )}

            {refinedInfographicPrompt !== null && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-[600px] p-6 flex flex-col gap-4 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                                <Sparkles className="text-blue-600" size={20} />
                                Step 1: AI Refinement
                            </h3>
                            <button onClick={() => setRefinedInfographicPrompt(null)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>
                        <p className="text-sm text-slate-500">The AI has refined your selected text into a visual prompt. You can adjust it below.</p>
                        <textarea
                            autoFocus
                            className="text-black w-full h-48 p-4 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium resize-none shadow-inner"
                            value={refinedInfographicPrompt}
                            onChange={(e) => setRefinedInfographicPrompt(e.target.value)}
                        />
                        <div className="flex justify-end gap-3 mt-2">
                            <button
                                onClick={() => setRefinedInfographicPrompt(null)}
                                className="flex-1 flex items-center justify-center px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Discard
                            </button>
                            <button
                                onClick={() => {
                                    const finalPrompt = refinedInfographicPrompt;
                                    setRefinedInfographicPrompt(null);
                                    handleGenerateInfographic(finalPrompt);
                                }}
                                className="px-6 py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg shadow-blue-500/20 transition-all active:scale-95 flex-1 flex items-center justify-center gap-2 uppercase tracking-wide"
                            >
                                <PieChart size={18} />
                                Generate Infographic
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {(showInfographicPreview && pendingInfographic) && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white rounded-2xl shadow-3xl max-w-[90vw] max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 border border-slate-200">
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white">
                            <h3 className="text-lg font-bold text-slate-800">Preview Infographic</h3>
                            <button onClick={() => setPendingInfographic(null)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors">
                                <Plus className="rotate-45" size={20} />
                            </button>
                        </div>

                        <div className="p-8 bg-slate-100 flex items-center justify-center overflow-auto min-h-[300px] relative">
                            {previewLoading && !infographicError && (
                                <div className="absolute inset-0 flex items-center justify-center bg-slate-100/50 z-10">
                                    <RefreshCw className="animate-spin text-slate-400" size={32} />
                                </div>
                            )}
                            <img
                                src={getProxiedUrl(pendingInfographic)}
                                alt="Generated Infographic"
                                className={cn(
                                    "max-w-full max-h-[60vh] rounded-lg shadow-2xl ring-4 ring-white transition-opacity duration-300",
                                    (infographicError || previewLoading) ? "opacity-0" : "opacity-100"
                                )}
                                onLoad={() => setPreviewLoading(false)}
                                onError={() => {
                                    setInfographicError(true);
                                    setPreviewLoading(false);
                                }}
                            />
                            {infographicError && (
                                <div className="flex flex-col items-center gap-4 text-slate-400 p-12 text-center animate-in fade-in duration-500">
                                    <div className="p-4 bg-slate-200/50 rounded-full">
                                        <PieChart size={48} className="opacity-40" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-slate-600">Failed to load preview</p>
                                        <p className="text-xs max-w-[300px] mt-1">The URL might be invalid or restricted. Ensure it's a valid image format.</p>
                                    </div>
                                    <code className="text-[10px] bg-slate-200/50 px-2 py-1 rounded break-all max-w-[400px]">
                                        {pendingInfographic}
                                    </code>
                                </div>
                            )}
                        </div>

                        <div className="p-6 bg-white border-t border-slate-100 flex flex-col gap-4">
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => handleFinalizeInfographic('insert')}
                                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-500/20 transition-all flex items-center justify-center gap-2"
                                >
                                    <Check size={18} />
                                    Insert infographic
                                </button>
                                {selectedElementId && presentation.slides[currentSlideIndex].elements.find(e => e.id === selectedElementId)?.type === 'image' && (
                                    <button
                                        onClick={() => handleFinalizeInfographic('replace')}
                                        className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-black rounded-xl font-bold shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2"
                                    >
                                        <RefreshCw size={18} />
                                        Replace existing
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => handleFinalizeInfographic('discard')}
                                className="w-full px-6 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                            >
                                <Trash2 size={18} />
                                Discard
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isPreviewMode && (
                <PresenterMode
                    presentation={presentation}
                    initialSlideIndex={currentSlideIndex}
                    onClose={handleClosePresenter}
                />
            )}

            <Snackbar
                isOpen={snackbar.open}
                message={snackbar.message}
                type={snackbar.type}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
};
